import { failureFromPayload, type AssistantFailure } from "./client";
import {
  MAX_MESSAGE_LENGTH,
  SUPPORTED_CONTRACT_VERSION,
  type CardsEvent,
  type CatalogSearched,
  type StreamEvent,
  type SuggestionsPayload
} from "./contract";

/**
 * The conversation the drawer shows, as a pure reducer.
 *
 * Everything that decides what the drawer does after a contract event or a
 * failure lives here, so the rules (section 7.2: what each error code means for
 * the transcript, the composer, the stored session id and the typed message)
 * are testable without a DOM. The components only render this state; the
 * provider only feeds it.
 *
 * Copy is not in here: a {@link Notice} names what happened and the drawer
 * chooses the translated words.
 */

/** A `ui` component the drawer renders as part of an answer. */
export type AnswerPanel =
  | { streamId: string; component: "guide"; payload: unknown }
  | { streamId: string; component: "order_status"; payload: unknown };

/** The status line while a tool runs: the model's own label, or the tool name
 *  for the drawer's fallback copy. */
export type StatusLine =
  | { kind: "progress"; text: string }
  | { kind: "tool"; tool: string; label: string | null };

export type UserMessage = { id: string; role: "user"; text: string };

export type AssistantMessage = {
  id: string;
  role: "assistant";
  text: string;
  cards: CardsEvent[];
  panels: AnswerPanel[];
  suggestions: string[];
  /** Section 6: every catalog searched this turn. `null` until `done`. */
  catalogs: CatalogSearched[] | null;
  status: "streaming" | "done" | "failed";
  statusLine: StatusLine | null;
  /** Why the turn failed, shown after whatever was already received. */
  failure: AssistantFailure | null;
  /** The turn finished but the model ran out of room (`done` with
   *  `stop_reason: "max_tokens"`): the answer stops mid-thought. */
  truncated: boolean;
};

/** A turn the salon can send again: it failed, or it was cut short. */
export function isResendable(message: Message | undefined): boolean {
  return (
    message?.role === "assistant" &&
    (message.status === "failed" || message.truncated === true)
  );
}

export type Message = UserMessage | AssistantMessage;

/** What the banner above the composer says. */
export type Notice =
  | { kind: "token_expired" }
  | { kind: "busy_session" }
  | { kind: "busy_service" }
  | { kind: "mismatch" }
  | { kind: "outdated" }
  /** "Try again" found a draft over the length limit and sent nothing. */
  | { kind: "too_long" };

export type ConversationState = {
  sessionId: string | null;
  messages: Message[];
  /** A turn is running. The composer is locked until it ends. */
  inFlight: boolean;
  /** The server ended the conversation: read-only, offer a new one. */
  ended: { reason: string | null } | null;
  /** The message limit is reached until `resetAt` (ISO 8601 UTC). */
  limit: {
    window: "hour" | "day" | null;
    max: number | null;
    resetAt: string;
  } | null;
  notice: Notice | null;
  /** The text in the composer. Survives a refused send so nothing typed is lost. */
  draft: string;
  /** The service said `disabled`: the entry point goes away. */
  disabled: boolean;
};

export const initialConversation: ConversationState = {
  sessionId: null,
  messages: [],
  inFlight: false,
  ended: null,
  limit: null,
  notice: null,
  draft: "",
  disabled: false
};

export type ConversationAction =
  | {
      type: "send";
      text: string;
      userId: string;
      assistantId: string;
      /** Text the salon typed meanwhile, which stays in the composer (a resend
       *  of an older question must not clear it) and is merged behind the
       *  question if the send is refused. */
      draft: string;
    }
  | { type: "event"; event: StreamEvent }
  | { type: "failure"; failure: AssistantFailure }
  | { type: "stream_closed" }
  | { type: "set_draft"; draft: string }
  | { type: "draft_too_long" } // a resend was refused locally: the draft is too long
  | { type: "discard_failed_turn" } // a failed or cut-short turn, to resend
  | { type: "dismiss_notice" }
  | { type: "start_new" }
  | { type: "limit_expired" };

function lastAssistant(state: ConversationState): AssistantMessage | null {
  const last = state.messages[state.messages.length - 1];
  return last && last.role === "assistant" ? last : null;
}

function updateLast(
  state: ConversationState,
  update: (message: AssistantMessage) => AssistantMessage
): ConversationState {
  const last = lastAssistant(state);
  if (!last) return state;
  return {
    ...state,
    messages: [...state.messages.slice(0, -1), update(last)]
  };
}

/** Drop the user message and the answer slot of a turn the service refused. */
function withdrawTurn(state: ConversationState): {
  state: ConversationState;
  text: string;
} {
  const messages = [...state.messages];
  const answer = messages[messages.length - 1];
  if (answer?.role === "assistant") messages.pop();
  const asked = messages[messages.length - 1];
  let text = "";
  if (asked?.role === "user") {
    text = asked.text;
    messages.pop();
  }
  return { state: { ...state, messages }, text };
}

/**
 * Take back a turn the service refused and put its question into the composer.
 * If the composer already holds text (a follow-up typed while an older failed
 * turn was being sent again), the question goes first, then a blank line, then
 * that text, so neither is lost and the salon can edit them before sending.
 */
function withdrawIntoComposer(state: ConversationState): ConversationState {
  const { state: withdrawn, text } = withdrawTurn(state);
  const typed = state.draft;
  const draft =
    text === "" ? typed : typed.trim() === "" ? text : `${text}\n\n${typed}`;
  return { ...withdrawn, draft };
}

function numberField(fields: Record<string, unknown>, key: string) {
  const value = fields[key];
  return typeof value === "number" ? value : null;
}

function applyFailure(
  state: ConversationState,
  failure: AssistantFailure
): ConversationState {
  const ended: ConversationState = { ...state, inFlight: false };
  const { code, fields } = failure;

  switch (code) {
    case "limit_reached": {
      const resetAt =
        typeof fields.reset_at === "string" ? fields.reset_at : "";
      const window =
        fields.limit === "hour" || fields.limit === "day" ? fields.limit : null;
      return {
        ...withdrawIntoComposer(ended),
        limit: { window, max: numberField(fields, "max"), resetAt }
      };
    }
    case "session_ended": {
      // Drop the stored id, keep the transcript on screen read-only, and keep
      // what was typed for the next conversation.
      return {
        ...withdrawIntoComposer(ended),
        sessionId: null,
        ended: {
          reason: typeof fields.reason === "string" ? fields.reason : null
        }
      };
    }
    case "session_customer_mismatch": {
      // The stored session is someone else's: forget it and the transcript
      // that went with it, and start over on the next message.
      return {
        ...initialConversation,
        draft: withdrawIntoComposer(ended).draft,
        notice: { kind: "mismatch" }
      };
    }
    case "token_expired": {
      return {
        ...withdrawIntoComposer(ended),
        notice: { kind: "token_expired" }
      };
    }
    case "busy": {
      return {
        ...withdrawIntoComposer(ended),
        notice: {
          kind: fields.scope === "session" ? "busy_session" : "busy_service"
        }
      };
    }
    case "disabled": {
      return { ...withdrawIntoComposer(ended), disabled: true };
    }
    default: {
      // internal, invalid_request, network: the turn failed. Whatever arrived
      // stays on screen and the failure is shown after it, with a retry.
      return updateLast(ended, (message) => ({
        ...message,
        status: "failed",
        statusLine: null,
        failure
      }));
    }
  }
}

function applyEvent(
  state: ConversationState,
  event: StreamEvent
): ConversationState {
  switch (event.type) {
    case "session": {
      const outdated =
        event.data.contract_version > SUPPORTED_CONTRACT_VERSION
          ? { kind: "outdated" as const }
          : state.notice;
      return { ...state, sessionId: event.data.session_id, notice: outdated };
    }
    case "text_delta": {
      const text = typeof event.data.text === "string" ? event.data.text : "";
      return updateLast(state, (message) => ({
        ...message,
        text: message.text + text,
        statusLine: null
      }));
    }
    case "progress":
      return updateLast(state, (message) => ({
        ...message,
        statusLine: { kind: "progress", text: String(event.data.message ?? "") }
      }));
    case "tool_call":
      return updateLast(state, (message) => ({
        ...message,
        statusLine: {
          kind: "tool",
          tool: event.data.tool,
          label: event.data.label ?? null
        }
      }));
    case "tool_result":
      return updateLast(state, (message) => {
        const line = message.statusLine;
        const same =
          line !== null &&
          line.kind === "tool" &&
          line.tool === event.data.tool;
        return same ? { ...message, statusLine: null } : message;
      });
    case "cards":
      return updateLast(state, (message) => {
        const cards = message.cards.filter(
          (existing) => existing.cards_id !== event.data.cards_id
        );
        return { ...message, cards: [...cards, event.data], statusLine: null };
      });
    case "ui_partial":
    case "ui":
      return applyUi(state, event);
    case "done":
      return updateLast({ ...state, inFlight: false }, (message) => ({
        ...message,
        status: "done",
        statusLine: null,
        truncated: event.data.stop_reason === "max_tokens",
        catalogs: Array.isArray(event.data.catalogs_searched)
          ? event.data.catalogs_searched
          : []
      }));
    case "error":
      // The same validation as a pre-stream HTTP error: an unknown code is an
      // `internal` failure, and `message` is dropped.
      return applyFailure(state, failureFromPayload(event.data));
  }
}

function applyUi(
  state: ConversationState,
  event: Extract<StreamEvent, { type: "ui" | "ui_partial" }>
): ConversationState {
  const { component, payload, stream_id: streamId } = event.data;

  if (component === "suggestions") {
    const list = (payload as Partial<SuggestionsPayload> | null)?.suggestions;
    if (!Array.isArray(list)) return state;
    const suggestions = list
      .filter((item): item is string => typeof item === "string")
      .slice(0, 4);
    return updateLast(state, (message) => ({ ...message, suggestions }));
  }

  // The remaining components are replaced by later frames with the same id.
  // Anything else (comparison, plan, checkout, ...) is ignored.
  if (component !== "guide" && component !== "order_status") return state;
  const panel: AnswerPanel =
    component === "guide"
      ? { streamId, component, payload }
      : { streamId, component, payload };
  return updateLast(state, (message) => ({
    ...message,
    panels: [
      ...message.panels.filter((existing) => existing.streamId !== streamId),
      panel
    ]
  }));
}

export function newAnswer(id: string): AssistantMessage {
  return {
    id,
    role: "assistant",
    text: "",
    cards: [],
    panels: [],
    suggestions: [],
    catalogs: null,
    status: "streaming",
    statusLine: null,
    failure: null,
    truncated: false
  };
}

export function conversationReducer(
  state: ConversationState,
  action: ConversationAction
): ConversationState {
  switch (action.type) {
    case "send":
      return {
        ...state,
        messages: [
          ...state.messages,
          { id: action.userId, role: "user", text: action.text },
          newAnswer(action.assistantId)
        ],
        inFlight: true,
        draft: action.draft,
        notice: state.notice?.kind === "outdated" ? state.notice : null
      };
    case "event":
      // Frames after a terminal one, or with no turn running, are stale.
      return state.inFlight ? applyEvent(state, action.event) : state;
    case "failure":
      return state.inFlight ? applyFailure(state, action.failure) : state;
    case "stream_closed": {
      // The connection ended with neither `done` nor `error` (section 3).
      if (!state.inFlight) return state;
      return applyFailure(state, { code: "network", fields: {} });
    }
    case "set_draft":
      return {
        ...state,
        draft: action.draft,
        // The too-long notice is about the draft: shortening it ends it.
        notice:
          state.notice?.kind === "too_long" &&
          action.draft.trim().length <= MAX_MESSAGE_LENGTH
            ? null
            : state.notice
      };
    case "draft_too_long":
      return { ...state, notice: { kind: "too_long" } };
    case "discard_failed_turn": {
      if (!isResendable(state.messages[state.messages.length - 1])) {
        return state;
      }
      const { state: withdrawn, text } = withdrawTurn(state);
      return { ...withdrawn, draft: text };
    }
    case "dismiss_notice":
      return state.notice ? { ...state, notice: null } : state;
    case "start_new":
      return {
        ...initialConversation,
        draft: state.draft,
        notice: state.notice?.kind === "outdated" ? state.notice : null
      };
    case "limit_expired":
      return state.limit ? { ...state, limit: null } : state;
  }
}

/** Whether the composer accepts a message right now. */
export function canSend(state: ConversationState, now: number = Date.now()) {
  if (state.inFlight || state.ended || state.disabled) return false;
  if (state.notice?.kind === "busy_session") return false;
  if (state.limit && !isLimitOver(state.limit.resetAt, now)) return false;
  return true;
}

export function isLimitOver(resetAt: string, now: number = Date.now()) {
  const reset = Date.parse(resetAt);
  return Number.isNaN(reset) ? true : now >= reset;
}
